import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HdLocksKeysReadingPanelId, getHdLocksKeysReadingPanel } from './hd-locks-keys-reading-panels';
import { openHdLocksKeysReadingPanel } from './hd-locks-keys-reading-panel.component';

/**
 * Doorklik naar een Locks & Keys-vervolgpaneel. Twee vormen van hetzelfde
 * patroon: `card` (kaart onderaan het intro-paneel; een uitgelicht paneel
 * krijgt een opvallende kaart met hint) en `inline` (compacte doorlink onder
 * een intro-sectie). Label, hint en icoon komen uit het paneelregister.
 */
@Component({
  selector: 'hd-locks-keys-panel-link',
  imports: [SharedModule],
  templateUrl: './hd-locks-keys-panel-link.component.html',
  styleUrl: './hd-locks-keys-panel-link.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysPanelLinkComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly floatingPanels = inject(FloatingPanelService);

  readonly panel = input.required<HdLocksKeysReadingPanelId>();
  readonly variant = input<'card' | 'inline'>('card');

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly definition = computed(() => getHdLocksKeysReadingPanel(this.panel()));

  protected readonly highlighted = computed(() => this.variant() === 'card' && this.definition().highlighted);

  protected readonly label = computed(() => {
    const prefix = this.definition().textPrefix;
    const lang = this.lang();
    if (this.variant() === 'inline') {
      const inlineLabel = this.gateInfo.locksKeysTextIn(`${prefix}.inlineLinkLabel`, lang);
      if (inlineLabel) {
        return inlineLabel;
      }
    }
    return this.gateInfo.locksKeysTextIn(`${prefix}.linkLabel`, lang);
  });

  /** Tweede regel op de uitgelichte kaart; leeg voor gewone kaarten en inline-links. */
  protected readonly hint = computed(() =>
    this.highlighted()
      ? this.gateInfo.locksKeysTextIn(`${this.definition().textPrefix}.linkHint`, this.lang())
      : '',
  );

  protected open(): void {
    openHdLocksKeysReadingPanel(this.floatingPanels, this.gateInfo, this.lang(), this.panel());
  }
}
